import React, { useEffect, useRef, useState } from "react";
import { checkNumberInput } from "../Helpers/bookingRules";

// A number input that only ever holds a value inside its limits. It's a text
// input with a numeric keyboard (type="number" lets browsers accept "e", "-"
// and mouse-wheel changes, and can't stop "150" in a 0-100 field). Every
// keystroke and paste goes through checkNumberInput; a change that would
// break the limits is not applied, and a short note says why ("Maximum is
// 100", "Whole numbers only") so the refusal isn't silent.
//
// Props: value, onChange(nextString), min, max, decimals (0 = whole numbers),
// hint ("inline" shows the note under the field; "bubble" uses the browser's
// own field bubble, for inputs inside a Bootstrap input-group where an extra
// element would break the layout), plus any normal input props.
const NumberField = ({ value, onChange, min = 0, max, decimals = 0, hint = "inline", className = "form-control", ...rest }) => {
    const current = value === null || value === undefined ? "" : String(value);
    const ref = useRef(null);
    const timer = useRef(null);
    const [note, setNote] = useState(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const refuse = (reason) => {
        clearTimeout(timer.current);
        if (hint === "bubble") {
            const el = ref.current;
            if (!el) return;
            el.setCustomValidity(reason);
            el.reportValidity();
            timer.current = setTimeout(() => el.setCustomValidity(""), 2500);
        } else {
            setNote(reason);
            timer.current = setTimeout(() => setNote(null), 2500);
        }
    };

    const input = (
        <input
            ref={ref}
            type="text"
            inputMode={decimals > 0 ? "decimal" : "numeric"}
            autoComplete="off"
            className={className}
            value={current}
            onChange={(e) => {
                const { value: next, reason } = checkNumberInput(e.target.value, current, { min, max, decimals });
                if (reason) refuse(reason);
                else if (note) setNote(null);
                if (next !== current) onChange(next);
            }}
            {...rest}
        />
    );
    if (hint === "bubble") return input;
    return (
        <>
            {input}
            {note && <div className="nf-note" role="status" style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>{note}</div>}
        </>
    );
};

export default NumberField;
